/**
 * Templates Feature Types
 *
 * Type definitions for the templates feature using the V2 denormalized template system.
 * Templates are self-contained with inline section content and explicit permissions.
 */

import type {
  ConvertToOrgTemplateRequest,
  CreateNoteTemplateRequest,
  CreateOrgNoteTemplateRequest,
  EffectiveNoteTemplateResponse,
  EffectiveNoteTemplateSummary,
  ForkSectionRequest,
  NoteTemplate,
  NoteTemplate2,
  NoteTemplate3,
  NoteTemplateListResponse,
  NoteTemplateOwnerScope,
  NoteTemplateOwnerScope2,
  NoteTemplateOwnerScope3,
  NoteTemplateRevision,
  NoteTemplateRevision2,
  NoteTemplateRevisionSummary,
  NoteTemplateSection,
  NoteTemplateSection2,
  NoteTemplateSectionRequest,
  NoteTemplateSectionRequest2,
  OrgNoteTemplateInfo,
  OrgNoteTemplateListResponse,
  ReorderTemplatesRequest,
  SearchSectionsRequest,
  SectionLibraryItem,
  SectionLibraryItem2,
  SectionLibraryListResponse,
  SectionLibraryOwnerScope,
  SectionLibraryOwnerScope2,
  SectionLibraryPermissions,
  SectionLibraryPermissions2,
  SectionLibraryResponse,
  SectionLibraryResponse2,
  TemplateOrderItem,
  TemplatePermissions,
  TemplatePermissions2,
  UpdateNoteTemplateRequest,
  UpdateSectionContentRequest,
} from '@store/generated/templates';
import { decodeHtmlEntities } from '@utils/htmlEntities';

export type {
  ConvertToOrgTemplateRequest,
  CreateNoteTemplateRequest,
  CreateOrgNoteTemplateRequest,
  EffectiveNoteTemplateResponse,
  EffectiveNoteTemplateSummary,
  ForkSectionRequest,
  NoteTemplate,
  NoteTemplate2,
  NoteTemplate3,
  NoteTemplateListResponse,
  NoteTemplateOwnerScope,
  NoteTemplateOwnerScope2,
  NoteTemplateOwnerScope3,
  NoteTemplateRevision,
  NoteTemplateRevision2,
  NoteTemplateRevisionSummary,
  NoteTemplateSection,
  NoteTemplateSection2,
  NoteTemplateSectionRequest,
  NoteTemplateSectionRequest2,
  OrgNoteTemplateInfo,
  OrgNoteTemplateListResponse,
  ReorderTemplatesRequest,
  SearchSectionsRequest,
  SectionLibraryItem,
  SectionLibraryItem2,
  SectionLibraryListResponse,
  SectionLibraryOwnerScope,
  SectionLibraryOwnerScope2,
  SectionLibraryPermissions,
  SectionLibraryPermissions2,
  SectionLibraryResponse,
  SectionLibraryResponse2,
  TemplateOrderItem,
  TemplatePermissions,
  TemplatePermissions2,
  UpdateNoteTemplateRequest,
  UpdateSectionContentRequest,
};

/**
 * Owner scope types for templates
 * - 'user': Private to a single user
 * - 'org': Available to organization members
 * - 'public': Available to all users
 */
export type OwnerScope = 'user' | 'org' | 'public';

/**
 * Template source when creating new templates
 */
export type TemplateSource = 'scratch' | 'library' | 'fork';

/**
 * View modes for the templates page
 */
export type TemplatesViewMode =
  | 'empty' // No template selected, showing welcome view
  | 'editor' // Editing a selected template
  | 'library' // Browsing template library
  | 'organization'; // Managing organization templates (org admin only)

/**
 * Template with computed UI properties
 * Extends the API response with display helpers
 */
export interface TemplateWithMeta {
  /** The template entity (ownership, scoping) */
  template: {
    id: string;
    owner_scope: OwnerScope;
    user_id: string;
    organization_id?: string | null;
    current_version: number;
    allow_user_forks?: boolean;
    parent_template_id?: string | null;
    created_at: string;
    updated_at: string;
    deleted_at?: string | null;
  };
  /** The template revision (content) */
  revision: {
    template_id: string;
    version: number;
    title: string;
    description?: string | null;
    /** Optional - not included in summary responses */
    prompt?: string | null;
    /** Optional - not included in summary responses */
    sections?: Array<{
      id: string;
      title: string;
      order?: number;
      content: string;
      legacy_module_id?: string | null;
      example_output?: string | null;
    }>;
    /** Optional - not included in summary responses */
    metadata?: Record<string, unknown>;
    /** Optional - not included in summary responses */
    content?: string;
    draft?: boolean;
    created_by: string;
    created_at: string;
    updated_at: string;
    published_at?: string | null;
  };
  /** Computed permissions for the current user */
  permissions: {
    can_view?: boolean;
    can_edit?: boolean;
    can_delete?: boolean;
    can_fork?: boolean;
  };
  /** Display name for the template (from revision.title) */
  displayName: string;
  /** Human-readable scope label */
  scopeLabel: 'My Template' | 'Organization' | 'Library';
  /** Whether template is locked (cannot edit or fork) */
  isLocked: boolean;
  /** Display order for pinned templates (0-4) or null if unpinned */
  displayOrder: number | null;
}

/**
 * Section with UI-specific state (for template sections)
 */
export interface SectionWithMeta {
  /** Unique section ID */
  id: string;
  /** Display title */
  title: string;
  /** Sort order */
  order: number;
  /** Section content (generation prompt) */
  content: string;
  /** Legacy module ID for reference (if migrated) */
  legacyModuleId?: string | null;
  /** Example output showing what this section generates (from Template Builder or training) */
  exampleOutput?: string | null;
  /** Whether preview is expanded */
  showPreview?: boolean;
}

/**
 * Section Library item with computed UI properties
 */
export interface SectionLibraryWithMeta {
  /** The section data */
  section: SectionLibraryItem;
  /** Computed permissions for the current user */
  permissions: SectionLibraryPermissions;
  /** Human-readable scope label */
  scopeLabel: 'Public Library' | 'Organization' | 'My Section';
}

/**
 * Props for the main Templates page
 */
export interface TemplatesPageProps {
  /** Initial template ID to select (from URL params) */
  initialTemplateId?: string;
}

/**
 * Template editor state
 */
export interface TemplateEditorState {
  /** The template being edited */
  template: TemplateWithMeta | null;
  /** Whether template is loading */
  isLoading: boolean;
  /** Whether template has unsaved changes */
  hasChanges: boolean;
  /** Last save timestamp */
  lastSaved: Date | null;
  /** Whether title is in edit mode */
  isTitleEditing: boolean;
  /** Index where new section should be added */
  addSectionIndex: number | null;
}

/**
 * Template sidebar state
 */
export interface TemplateSidebarState {
  /** List of user templates */
  templates: TemplateWithMeta[];
  /** Currently selected template ID */
  selectedTemplateId: string | null;
  /** Whether sidebar is loading */
  isLoading: boolean;
  /** Current view mode */
  viewMode: TemplatesViewMode;
}

/**
 * Template library state
 */
export interface TemplateLibraryState {
  /** Available public templates */
  publicTemplates: TemplateWithMeta[];
  /** Search term */
  searchTerm: string;
  /** Selected category filter */
  categoryFilter: string | null;
  /** Whether library is loading */
  isLoading: boolean;
}

/**
 * Analytics event metadata for templates
 */
export interface TemplateAnalyticsMetadata {
  template_id?: string;
  owner_scope?: OwnerScope;
  template_name?: string;
  source?: TemplateSource;
  section_count?: number;
  action?: string;
}

/**
 * Helper function to get scope label for templates
 */
export const getScopeLabel = (scope: OwnerScope): 'My Template' | 'Organization' | 'Library' => {
  switch (scope) {
    case 'user':
      return 'My Template';
    case 'org':
      return 'Organization';
    case 'public':
      return 'Library';
  }
};

/**
 * Helper function to get scope label for sections
 */
export const getSectionScopeLabel = (
  scope: SectionLibraryOwnerScope,
): 'Public Library' | 'Organization' | 'My Section' => {
  switch (scope) {
    case 'user':
      return 'My Section';
    case 'org':
      return 'Organization';
    case 'public':
      return 'Public Library';
  }
};

/**
 * Helper function to check if template is locked
 */
export const isTemplateLocked = (permissions: {
  can_edit?: boolean;
  can_fork?: boolean;
}): boolean => {
  return !permissions.can_edit && !permissions.can_fork;
};

/**
 * Transform API response to TemplateWithMeta
 */
export const toTemplateWithMeta = (response: {
  template: TemplateWithMeta['template'];
  revision: TemplateWithMeta['revision'];
  permissions?: TemplateWithMeta['permissions'] | null;
  display_order?: number | null;
}): TemplateWithMeta => {
  const permissions: TemplateWithMeta['permissions'] = response.permissions ?? {};
  return {
    template: response.template,
    revision: response.revision,
    permissions,
    // Decode HTML entities (e.g. "&amp;" -> "&") so template names with an
    // ampersand render literally. See GDN-2914.
    displayName: decodeHtmlEntities(response.revision.title),
    scopeLabel: getScopeLabel(response.template.owner_scope),
    isLocked: isTemplateLocked(permissions),
    displayOrder: response.display_order ?? null,
  };
};

/**
 * Transform API response to SectionLibraryWithMeta
 */
export const toSectionLibraryWithMeta = (
  response: SectionLibraryResponse,
): SectionLibraryWithMeta => {
  return {
    // Decode HTML entities in the title (e.g. "&amp;" -> "&") so section
    // headings render literally on library cards. See GDN-2914.
    section: {
      ...response.section,
      title: decodeHtmlEntities(response.section.title),
    },
    permissions: response.permissions,
    scopeLabel: getSectionScopeLabel(response.section.owner_scope),
  };
};
